import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Divider, Form, Input, Upload, UploadFile } from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import {
	FileData,
	SiteReportBusinessActivity,
} from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { useCCAVisitReportVerificationPage } from './VisitReportVerificationPage';
import { submitCCASiteReportBusinessActivity } from '@/libs/fleet-api/services/visit-reports';

type SiteReportBusinessActivityForm = SiteReportBusinessActivity<'payload'> &
	Pick<SiteReportBusinessActivity, 'supportingDocument'>;

const CCASiteVisitReportActivity: FC<{ onSave: () => void }> = ({ onSave }) => {
	const { siteReport, surveyInformation, mainInformation } =
		useCCAVisitReportVerificationPage();
	const { form, fieldName } = useForm<SiteReportBusinessActivityForm>();

	const submitCCASiteReportBusinessActivityMutation = useMutation({
		mutationFn: submitCCASiteReportBusinessActivity,
	});

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			labelCol={{
				span: 10,
			}}
			initialValues={
				{
					id: siteReport?.id,
					surveyId: surveyInformation!.id,
					businessActivityVerification:
						siteReport?.businessActivityVerification,
					productionDataVsTargetVerification:
						siteReport?.productionDataVsTargetVerification,
					operationalConstraintsExplanation:
						siteReport?.operationalConstraintsExplanation,
					additionalBusinessActivityInfo:
						siteReport?.additionalBusinessActivityInfo,
					supportingDocument: siteReport?.supportingDocument,
					businessActivityVerificationNotes:
						siteReport?.businessActivity?.businessActivityVerificationNotes,
				} as SiteReportBusinessActivityForm
			}
			onFinish={async (values) => {
				await submitCCASiteReportBusinessActivityMutation.mutateAsync(values);
				onSave?.();
			}}
		>
			<Form.Item
				name={fieldName('id')}
				noStyle
			/>
			<Form.Item
				name={fieldName('surveyId')}
				noStyle
			/>
			<Form.Item
				name={fieldName('businessActivityVerification')}
				label="Verifikasi Aktivitas Usaha"
				labelCol={{
					span: 6,
				}}
				rules={[rule.required()]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Divider />
			<Form.Item
				name={fieldName('productionDataVsTargetVerification')}
				label="Verifikasi Data Produksi vs Target"
				labelCol={{
					span: 6,
				}}
				rules={[rule.required()]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Divider />
			<Form.Item
				name={fieldName('operationalConstraintsExplanation')}
				label="Penjelasan Hambatan Operasional"
				labelCol={{
					span: 6,
				}}
				rules={[rule.required()]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Divider />
			<Form.Item
				name={fieldName('supportingDocument')}
				label="Upload Dokumen Pendukung"
				labelCol={{ span: 6 }}
				rules={[rule.required()]}
				getValueProps={(value) => {
					const file = value as FileData;
					return {
						fileList: file?.id
							? ([
									{
										uid: file.id,
										name: file.name,
										thumbUrl: file.url,
									},
								] as UploadFile[])
							: [],
					};
				}}
				normalize={({ file }: { file: UploadFile }) => {
					return {
						id: file.uid,
						format: file.type,
						name: file.name,
						url: file.thumbUrl,
					} as FileData;
				}}
			>
				<Upload.Dragger
					listType="picture"
					maxCount={1}
					beforeUpload={() => false}
					onRemove={() => {
						form.setFieldValue(fieldName('supportingDocument'), null);
						return false;
					}}
					onChange={async ({ file }) => {
						const fileid = Date.now().toString();
						const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/supporting-document`;

						try {
							const url = await uploadMutation.mutateAsync({
								file: file as unknown as File,
								fileid,
								filepath,
							});

							form.setFieldValue(fieldName('supportingDocument'), {
								format: 'jpeg',
								id: fileid,
								name: file.name,
								path: filepath,
								url,
							});
						} catch {
							form.setFieldValue(fieldName('supportingDocument'), null);
						}
					}}
				>
					{uploadMutation.isPending ? (
						<LoadingOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					) : (
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					)}
				</Upload.Dragger>
			</Form.Item>
			<Divider />
			<Form.Item
				name={fieldName('additionalBusinessActivityInfo')}
				label="FRM Notes"
				labelCol={{
					span: 6,
				}}
			>
				{form.getFieldValue(fieldName('additionalBusinessActivityInfo'))}
			</Form.Item>
			<Divider />
			<Form.Item
				name={fieldName('businessActivityVerificationNotes')}
				label="Verification Notes"
				labelCol={{
					span: 6,
				}}
				rules={[rule.required()]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Button
				type="primary"
				style={{
					width: 200,
					display: 'block',
					marginLeft: 'auto',
					marginTop: 20,
				}}
				loading={submitCCASiteReportBusinessActivityMutation.isPending}
				onClick={() => {
					form.submit();
				}}
			>
				SAVE
			</Button>
		</Form>
	);
};

export default CCASiteVisitReportActivity;
